import _ from 'underscore';
import decimalUtil from '@bingads-webui-component-legacy/humanize/decimal';
import config from '@bingads-webui-component-legacy/config/index';
import template from '@bingads-webui-static-legacy/bulk-edit/template/campaign/association.pug';
import gridHelper from '@bingads-webui-static-legacy/shared/view/gridHelper';
import { errorEnum } from '@bingads-webui-campaign-legacy/audience-association/model/errorEnum';
import AssociationError from '@bingads-webui-campaign-legacy/audience-association/model/associationError';
import pageContext from '@bingads-webui-static-legacy/page-context';
import { levelAtConstant } from '@bingads-webui-campaign/scope-constants';

import CommonAssociationView from '@bingads-webui-campaign-legacy/audience-bulk-edit-common-association/view/association';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!campaignui/feature/audience/association');


export default class View extends CommonAssociationView {
  initialize(options) {
    super.initialize(_.extend({}, options, {
      template,
      validate: this.validateCPSelection,
      onSuccessCallback: this._onSuccess,
      formatAssociation: this.formatCampaignAssociation,
      entityLevel: levelAtConstant.CAMPAIGN,
    }));

    // overwrite onSuccessCallback with customized callback
    this.onSuccessCallback = options.onSuccessCallback || this.onSuccessCallback;

    // Enum of Campaign Selection Validation Results
    this.cpValidationEnum = {
      'OK': 0,
      'TooManyAssociations': 1,
      'NoChosenList': 5,
      'ListConflicting': 6,
      'DeliveryChannel': 7,
    };
  }

  _onSuccess() {
    // Show association success information message
    window.App.trigger('campaignRemarketingAssociated');
  }

  formatCampaignAssociation(rows) {
    if (rows) {
      _.each(rows, (value) => {
        value.Entity = _.find(value.EntityKey, (item) => {
          return item.Key === 'CampaignName';
        });

        value.data = [];
        value.data.push({ value: value.Entity && value.Entity.Value ? value.Entity.Value : '', isHtml: false });

        if (value.Changes && value.Changes.length) {
          value.data.push({ value: '-', isHtml: false });
          value.data.push({ value: value.Changes[0].NewValue, isHtml: false });
        } else {
          value.data.push({ value: '', isHtml: false });
          value.data.push({ value: '', isHtml: false });
        }
      });
    }

    return rows;
  }

  validateCPSelection(selectedListsCount) {
    // Returns OK if the selection passes all validations
    let results = [];

    // Clears error message before validation
    this.doubleListBoxView.clearAllErrors();

    if (!_.isNull(selectedListsCount)) {
      if (selectedListsCount === 0) {
        results.push({ error: this.cpValidationEnum.NoChosenList });
      } else {
        // check if there is id conflicting in targeting and exclusion
        let { associationList, exclusionList } = this.doubleListBoxView.getLists();

        let associationListIds  = _.pluck(associationList, 'Id'),
          exclusionListIds  = _.pluck(exclusionList, 'Id'),
          allListIds = _.union(associationListIds, exclusionListIds),
          allList = _.flatten(_.union(associationList, exclusionList));

        if (allListIds.length !== (associationListIds.length + exclusionListIds.length)) {
          let conflictList = _.chain(associationListIds)
            .intersection(exclusionListIds)
            .map((id) => _.findWhere(allList, { Id: id }))
            .pluck('Name')
            .value();

          results.push({ error: this.cpValidationEnum.ListConflicting, param: conflictList });
        }

        // Check whether there are too many association generated by the selection
        // This is only checked when called from submitJob with selectedListsCount provided
        const selectCampaignsCount = gridHelper.getSelectedRowsCount(this.grid);
        const audienceSettings = config.get('audienceSettings');
        const maxAudienceCriterionMaxLimit = audienceSettings.BulkAudienceCriterionMaxLimit;

        if ((selectedListsCount * selectCampaignsCount) > maxAudienceCriterionMaxLimit) {
          results.push({ error: this.cpValidationEnum.TooManyAssociations });
        }
      }
    }

    if (this.IsAIMCampaignEnabled && this.scope.currentScope !== 'campaign') {
      results.push({ error: this.cpValidationEnum.DeliveryChannel });
    }

    if (_.size(results) === 0) {
      return true;
    } else {
      this.showErrorMessage(results);

      return this.doubleListBoxView.anyCriticalErrors();
    }
  }

  submitJob() {
    super.submitJob();
  }

  showErrorMessage(validationResults) {
    _.each(validationResults, (validationResult) => {
      switch (validationResult.error) {
      case this.cpValidationEnum.TooManyAssociations: {
        const audienceSettings = config.get('audienceSettings');
        const maxAudienceCriterionMaxLimit = audienceSettings.BulkAudienceCriterionMaxLimit;

        // shared error message with AdGroup
        const limitMessage = i18n.get('AudiencesAssociation_ListToCampaign_Error_ExceedMaxLimit').replace('{0}', decimalUtil.fromFloatToString(maxAudienceCriterionMaxLimit, 0));
        let limitError = new AssociationError(errorEnum.ExceedMaxLimit.id,
          { scope: this.scope.currentScope, isCritical: true, level: this.entityLevel }, null, limitMessage);

        this.doubleListBoxView.addError(limitError);
        break;
      }
      case this.cpValidationEnum.NoChosenList: {
        let noChosenError = new AssociationError(errorEnum.NoChosenList.id,
          { scope: this.scope.currentScope, isCritical: true, level: this.entityLevel });

        this.doubleListBoxView.addError(noChosenError);
        break;
      }
      case this.cpValidationEnum.ListConflicting: {
        // Shared error message with AdGroup
        let errormessagetemp = _.template(i18n.get('AudiencesAssociation_ListToAdGroup_Error_List_Conflicting'));
        // Note that even though the variables have "Adgroup" in them, they won't show up in the final message:
        //  "The same remarketing list can’t be added to both {{AdgroupTargeting}} and {{AdgroupExclusions}}. Remove the following lists from either targeting or exclusions"
        const messageContent = errormessagetemp({
          AdgroupTargeting: `${i18n.get('Audiences_Campaign_Association_Title')}`,
          AdgroupExclusions: `${i18n.get('Audiences_Campaign_Exclusion_Title')}`,
        });
        let message = `${messageContent}: ${validationResult.param.join(', ')}`;
        let conflictingError = new AssociationError(errorEnum.ListConflicting.id,
          { scope: this.scope.currentScope, isCritical: true, level: this.entityLevel }, null, message);

        this.doubleListBoxView.addError(conflictingError);
        break;
      }
      case this.cpValidationEnum.DeliveryChannel: {
        if (!pageContext.Permissions.IsProductAudienceForSearchEnabled) {
          this.doubleListBoxView.addError(new AssociationError(
            errorEnum.DeliveryChannel.id,
            { scope: this.scope.currentScope, isCritical: false, level: this.entityLevel }
          ));
        }
        break;
      }
      default:
        break;
      }
    });
  }
}
